import { Button, Checkbox, Modal } from '@arvoreeducacao/bonsai-ds'
import React, { useEffect, useState } from 'react'
import {
  Container,
  HeaderContainer,
  Title,
  Subtitle,
  JobOptionCard,
  JobOptionInfo,
  JobOptionTitle,
  JobOptionDescription,
  ListJobs,
  WrapperFooter
} from './UpdateJobPositionOrAreaModal.styles'
import {
  JOB_OPTIONS,
  JobOption,
  TITLE_AND_SUBTITLE_BY_JOB_OPTION
} from './constants'
import {
  useGetUserJobPositionsAndRolesQuery,
  useQualifyJobDescriptionMutation,
  type JobArea,
  type JobPosition
} from '__gql-generated__/types'
import { useTranslation } from 'i18n'
import useToast from 'utils/hooks/use-toast'

const UpdateJobPositionOrAreaModal = ({
  jobOption,
  isOpen,
  onClose
}: {
  jobOption: JobOption
  isOpen: boolean
  onClose: () => void
}) => {
  const { data } = useGetUserJobPositionsAndRolesQuery()
  const user = data?.me
  const [selectedJobOptions, setSelectedJobOptions] = useState<
    JobPosition[] | JobArea[] | []
  >([])
  const [updateQualifyJobDescription] = useQualifyJobDescriptionMutation()
  const { toastMessage } = useToast()
  const { t } = useTranslation()

  const userJobOptions =
    jobOption === JobOption.JOB_POSITIONS ? user?.jobPositions : user?.jobAreas

  useEffect(() => {
    if (userJobOptions) {
      setSelectedJobOptions(userJobOptions)
    }
  }, [userJobOptions])

  const handleChangeJobOption = (jobOption: JobPosition | JobArea) => {
    setSelectedJobOptions(prev => {
      const isJobOptionSelected = prev.some(item => item === jobOption)

      if (isJobOptionSelected) {
        return prev.filter(item => item !== jobOption)
      }

      return [...prev, jobOption] as typeof prev
    })
  }

  const handleCloseModal = () => {
    onClose()
    setSelectedJobOptions(userJobOptions)
  }

  const handleUpdateQualifyJobDescription = () => {
    updateQualifyJobDescription({
      variables: {
        jobPositions:
          jobOption === JobOption.JOB_POSITIONS
            ? (selectedJobOptions as JobPosition[])
            : user.jobPositions,
        jobAreas:
          jobOption === JobOption.JOB_AREAS
            ? (selectedJobOptions as JobArea[])
            : user.jobAreas
      },
      refetchQueries: ['GetUserJobPositionsAndRoles']
    })
      .then(() => {
        toastMessage({
          type: 'success',
          message: t('Alteração salva com sucesso!')
        })
        handleCloseModal()
      })
      .catch(() => {
        toastMessage({
          type: 'error',
          message: t('Não pudemos salvar a alteração. Tente novamente.')
        })
      })
  }

  return (
    <Modal
      size="xlarge"
      type="standard"
      isOpen={isOpen}
      titleSize={4}
      onClose={handleCloseModal}
      title={t(TITLE_AND_SUBTITLE_BY_JOB_OPTION[jobOption].titleModal)}
      hasDesktopCloseButton
      sheetHeaderProps={{
        type: 'close',
        title: t(TITLE_AND_SUBTITLE_BY_JOB_OPTION[jobOption].titleModal),
        onClose: handleCloseModal
      }}
      footerElements={
        <WrapperFooter>
          <Button
            text={t('Cancelar')}
            theme={{ type: 'secondary', size: 'large' }}
            onClick={handleCloseModal}
          />
          <Button
            disabled={userJobOptions === selectedJobOptions}
            text={t('Salvar')}
            theme={{ type: 'primaryLivros', size: 'large' }}
            onClick={handleUpdateQualifyJobDescription}
          />
        </WrapperFooter>
      }
    >
      <Container>
        <HeaderContainer>
          <Title>{t(TITLE_AND_SUBTITLE_BY_JOB_OPTION[jobOption].title)}</Title>
          <Subtitle>
            {t(TITLE_AND_SUBTITLE_BY_JOB_OPTION[jobOption].subtitle)}
          </Subtitle>
        </HeaderContainer>
        <ListJobs>
          {JOB_OPTIONS[jobOption]?.map(jobOption => {
            const isJobOptionSelected = selectedJobOptions?.some(
              item => item === jobOption.value
            )

            return (
              <JobOptionCard
                key={jobOption.value}
                isSelected={isJobOptionSelected}
                onClick={() => handleChangeJobOption(jobOption.value)}
              >
                <Checkbox
                  item={{
                    id: jobOption.value,
                    label: jobOption.label
                  }}
                  onChange={() => {}}
                  defaultValue={isJobOptionSelected}
                  contained
                  hideText
                />
                <JobOptionInfo>
                  <JobOptionTitle>{t(jobOption.label)}</JobOptionTitle>
                  {jobOption?.description && (
                    <JobOptionDescription>
                      {t(jobOption.description)}
                    </JobOptionDescription>
                  )}
                </JobOptionInfo>
              </JobOptionCard>
            )
          })}
        </ListJobs>
      </Container>
    </Modal>
  )
}

export default UpdateJobPositionOrAreaModal
